import { useMemo } from "react";
import { Package, Gavel, Clock } from "lucide-react";

import { useTranslation, type Language } from "../../i18n";
import { statusTokenPrefix } from "./laneStatus";
import { statusLabel } from "./laneStatusConfig";
import { buildZoneGroups, zoneShortLabel } from "./laneGrouping";
import type { Lane } from "./useLanes";

interface LaneFloorProps {
    lanes: Lane[];
    onLaneClick: (lane: Lane) => void;
}

const formatDate = (iso: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso + "T00:00:00");
    const dd = d.getDate().toString().padStart(2, "0");
    const mm = (d.getMonth() + 1).toString().padStart(2, "0");
    return `${dd}/${mm}`;
};

const LaneFloor = ({ lanes, onLaneClick }: LaneFloorProps) => {
    const { t, language } = useTranslation();
    const groups = useMemo(() => buildZoneGroups(lanes), [lanes]);

    if (lanes.length === 0) {
        return <div className="lanes-empty-state">{t("lanes_empty")}</div>;
    }

    return (
        <div className="lanes-floor">
            {groups.map(g => (
                <div key={g.zone} className="lanes-floor-strip">
                    <div className="lanes-floor-rail">
                        <div className="lanes-floor-rail-letter">{zoneShortLabel(g.zone)}</div>
                        <div className="lanes-floor-rail-count">
                            {g.lanes.length} {t("lanes_zone_lanes_suffix")}
                        </div>
                    </div>
                    <div className="lanes-floor-grid">
                        {g.lanes.map(l => (
                            <LaneTile
                                key={l.barcode}
                                lane={l}
                                onClick={() => onLaneClick(l)}
                                language={language}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

interface LaneTileProps {
    lane: Lane;
    onClick: () => void;
    language: Language;
}

const LaneTile = ({ lane, onClick, language }: LaneTileProps) => {
    const isIdle = lane.status === "lane-empty";
    const prefix = statusTokenPrefix(lane.status);
    // Idle lanes go plain-white + dashed so they visually recede; the
    // rest get the family's pill colour set so the floor reads like a
    // colour map of the warehouse's state.
    const style: React.CSSProperties = isIdle
        ? {}
        : {
            background: `var(--status-${prefix}-bg)`,
            borderColor: `var(--status-${prefix}-border)`,
            color: `var(--status-${prefix}-fg)`,
        };

    return (
        <button
            type="button"
            className={`lanes-floor-tile${isIdle ? " lanes-floor-tile--idle" : ""}`}
            style={style}
            onClick={onClick}
        >
            <div className="lanes-floor-tile-top">
                <span className="lanes-floor-tile-barcode">{lane.barcode}</span>
            </div>
            <div className="lanes-floor-tile-status">
                {statusLabel(lane.status, language)}
            </div>
            <div className="lanes-floor-tile-stats">
                <span className={`lanes-floor-tile-stat${lane.totes === 0 ? " lanes-floor-tile-stat--zero" : ""}`}>
                    <Package size={12} strokeWidth={1.8} />
                    {lane.totes === 0 ? "—" : lane.totes}
                </span>
                <span className={`lanes-floor-tile-stat${lane.auctions === 0 ? " lanes-floor-tile-stat--zero" : ""}`}>
                    <Gavel size={12} strokeWidth={1.8} />
                    {lane.auctions === 0 ? "—" : lane.auctions}
                </span>
                <span className={`lanes-floor-tile-stat${lane.auction_end_date == null ? " lanes-floor-tile-stat--zero" : ""}`}>
                    <Clock size={12} strokeWidth={1.8} />
                    {formatDate(lane.auction_end_date)}
                </span>
            </div>
        </button>
    );
};

export default LaneFloor;
